import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { platformOpen } from '@/lib/platformOpen';
import { FEATURE_FLAGS } from '@/config/featureFlags';
import type { ActionDefinition } from '@/lib/personalActions';
import {
  type ExpansionItem,
  type ExpansionProvider,
  type ExpansionRequest,
  type ExpansionAccess,
  type ExpansionDeleteAccess,
  expansionAccess,
  expansionScopes,
  isExpansionProvider,
  expansionWrite,
  isExpansionReadValidationAccount,
} from '../../supabase/functions/_shared/expansionDomain';
export { EXPANSION_PROVIDERS, isExpansionProvider } from '../../supabase/functions/_shared/expansionDomain';
export type {
  ExpansionItem,
  ExpansionProvider,
  ExpansionRequest,
  ExpansionAccess,
  ExpansionDeleteAccess,
} from '../../supabase/functions/_shared/expansionDomain';
export const expansionVisible = (provider: string, owner?: string | null) =>
  isExpansionProvider(provider) && (isExpansionReadValidationAccount(provider, owner) ||
  FEATURE_FLAGS.EXPANSION_CATALOG_ENABLED &&
  ({
    dropbox: FEATURE_FLAGS.DROPBOX_ENABLED,
    todoist: FEATURE_FLAGS.TODOIST_ENABLED,
    trello: FEATURE_FLAGS.TRELLO_ENABLED,
    slack: FEATURE_FLAGS.SLACK_ENABLED,
    microsoft_teams: FEATURE_FLAGS.MICROSOFT_TEAMS_ENABLED,
  }[provider]));
export const expansionWritesVisible = (provider: string) =>
  FEATURE_FLAGS.PERSONAL_ACTION_ENGINE_ENABLED && expansionVisible(provider);
export const expansionBenefitKey = (provider: string) =>
  expansionWritesVisible(provider)
    ? 'expansion.benefit.' + provider
    : provider === 'microsoft_teams'
    ? 'expansion.teamsReadBenefit'
    : 'expansion.readBenefit.' + provider;
export async function expansionSession(owner?: string, checkCurrent?: () => Promise<void>) {
  if (checkCurrent) await checkCurrent();
  const { data: { session }, error } = await supabase.auth.getSession();
  if (checkCurrent) await checkCurrent();
  if (error || !session?.access_token || owner && session.user.id !== owner) throw new Error('invalid_session');
  return { owner: session.user.id, token: session.access_token };
}
async function invoke(owner: string, name: string, body: Record<string, unknown>, checkCurrent?: () => Promise<void>) {
  const session = await expansionSession(owner, checkCurrent);
  const { data, error } = await supabase.functions.invoke(name, {
    body,
    headers: { Authorization: 'Bearer ' + session.token },
  });
  await expansionSession(owner, checkCurrent);
  if (error) {
    let code = 'request_failed';
    try {
      code = (await error.context?.json())?.error ?? code;
    } catch { /* offline */ }
    throw new Error(code);
  }
  if (!data) throw new Error('request_failed');
  return data;
}
export interface ExpansionFeed {
  ok: true;
  user_id: string;
  account_id: string;
  display_name: string;
  can_write: boolean;
  items?: ExpansionItem[];
  item?: ExpansionItem;
  next_cursor?: string | null;
  sampled?: boolean;
}
export async function expansionRequest(
  owner: string,
  body: Partial<ExpansionRequest> & Pick<ExpansionRequest, 'provider' | 'action'>,
): Promise<ExpansionFeed> {
  if (!expansionVisible(body.provider, owner) ||
    expansionWrite(body.action) && !expansionVisible(body.provider)) throw new Error('provider_disabled');
  const data = await invoke(owner, 'expansion-executor', body);
  if (data.ok !== true || data.user_id !== owner) throw new Error('invalid_session');
  return data;
}
export async function connectExpansion(provider: ExpansionProvider, requestedAccess: boolean | ExpansionAccess | ExpansionDeleteAccess = false, expectedOwner?: string, checkCurrent?: () => Promise<void>) {
  const access = requestedAccess === 'delete' ? requestedAccess : expansionAccess(requestedAccess);
  expansionScopes(provider, access);
  const { owner } = await expansionSession(expectedOwner, checkCurrent);
  if (!expansionVisible(provider, owner) || access !== 'read' && !expansionVisible(provider)) throw new Error('provider_disabled');
  const consent = access === 'delete'
    ? { provider, access, delete_access: true }
    : typeof requestedAccess === 'boolean'
    ? { provider, write_access: requestedAccess } : { provider, access };
  const data = await invoke(owner, 'expansion-oauth-init', consent, checkCurrent);
  const u = new URL(data.authorize_url);
  const origins: Record<ExpansionProvider, string> = {
    dropbox: 'https://www.dropbox.com',
    todoist: 'https://app.todoist.com',
    trello: 'https://auth.atlassian.com',
    slack: 'https://slack.com',
    microsoft_teams: 'https://login.microsoftonline.com',
  };
  if (u.origin !== origins[provider] || u.username || u.password) throw new Error('invalid_authorize_url');
  if (checkCurrent) await checkCurrent();
  await platformOpen(u.href);
  if (checkCurrent) await checkCurrent();
}
export async function disconnectExpansion(provider: ExpansionProvider, expectedOwner?: string) {
  const { owner } = await expansionSession(expectedOwner);
  const { data, error } = await supabase.from('user_connections').delete().eq('user_id', owner).eq(
    'provider',
    provider,
  ).select('id');
  await expansionSession(owner);
  if (error || data?.length !== 1) throw new Error('disconnect_failed');
}
export async function createExpansionPlan(
  owner: string,
  title: string,
  definition: ActionDefinition,
  key: string,
) {
  await expansionSession(owner);
  const existing = await (supabase as SupabaseClient).from('personal_action_plans').select('id').eq(
    'user_id',
    owner,
  ).eq('client_action_id', key).maybeSingle();
  if (existing.error) throw existing.error;
  if (existing.data) {
    await expansionSession(owner);
    return existing.data.id as string;
  }
  const result = await invoke(owner, 'personal-action-engine', {
    op: 'create',
    title: title.slice(0, 180),
    steps: [definition],
    client_action_id: key,
  });
  if (result?.plan?.user_id !== owner) throw new Error('invalid_session');
  // Preparing is local validation and persistence; it never calls the provider.
  // If preparation is interrupted, the existing V4 workspace can resume it.
  try {
    await invoke(owner, 'personal-action-engine', {
      op: 'prepare',
      plan_id: result.plan.id,
      revision: result.plan.revision,
      step: 0,
    });
  } catch { /* keep the recoverable plan */ }
  await expansionSession(owner);
  return result.plan.id as string;
}
